import { useState, useEffect, useCallback, useRef } from "react";

const API      = process.env.REACT_APP_API_URL || "http://localhost:4000/api";
const getToken = () => localStorage.getItem("sellsync_token");
const authFetch = async (path, opts = {}) => {
  const res  = await fetch(API + path, { ...opts, headers: { "Content-Type":"application/json", Authorization:"Bearer "+getToken(), ...opts.headers } });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Failed");
  return data;
};

const PLATFORMS = [
  { id:"shopify",  name:"Shopify",  color:"#5a8a00" },
  { id:"amazon",   name:"Amazon",   color:"#c45000" },
  { id:"flipkart", name:"Flipkart", color:"#1b57c0" },
  { id:"myntra",   name:"Myntra",   color:"#b5003c" },
  { id:"meesho",   name:"Meesho",   color:"#9f2089" },
];

const CATEGORIES = ["Formal Belts","Casual Belts","Reversible Belts","Autolock Belts","Canvas Belts","Kids Belts","Tactical Belts","Designer Belts","Gift Sets","Accessories"];

const TABS = [
  { id:"catalog",  label:"📦 Product Catalog" },
  { id:"ai",       label:"🤖 AI Content"       },
  { id:"images",   label:"🖼️ Image Formatter"  },
  { id:"skumap",   label:"🔗 SKU Mapping"      },
  { id:"bulk",     label:"📋 Bulk Operations"  },
];

const S = {
  card:   { background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", marginBottom:12 },
  hdr:    { padding:"11px 16px", borderBottom:"1px solid #e5e7eb", fontWeight:700, fontSize:13, display:"flex", alignItems:"center", justifyContent:"space-between" },
  th:     { padding:"7px 12px", textAlign:"left", fontSize:10, fontWeight:700, color:"#9ca3af", background:"#f9fafb", whiteSpace:"nowrap" },
  td:     { padding:"7px 12px", fontSize:12, borderBottom:"1px solid #f3f4f6", verticalAlign:"middle" },
  btn:    (c="#F47920") => ({ padding:"7px 14px", borderRadius:7, fontSize:11, cursor:"pointer", background:c, color:"#fff", border:"none", fontFamily:"inherit", fontWeight:600 }),
  btnOut: { padding:"6px 12px", borderRadius:7, fontSize:11, cursor:"pointer", background:"#fff", color:"#374151", border:"1px solid #d1d5db", fontFamily:"inherit" },
  input:  { padding:"7px 10px", borderRadius:7, border:"1px solid #d1d5db", fontSize:12, fontFamily:"inherit", outline:"none", boxSizing:"border-box" },
  label:  { fontSize:11, color:"#6b7280", fontWeight:600, display:"block", marginBottom:3 },
  tag:    (c="#F47920") => ({ background:c+"18", color:c, padding:"2px 8px", borderRadius:10, fontSize:10, fontWeight:700 }),
};

const EMPTY_PRODUCT = {
  name:"", sku:"", category:"Formal Belts", mrp:"", price:"", costPrice:"",
  hsn:"42033000", gstRate:"18", material:"Genuine Leather", color:"", size:"32-44",
  brand:"Leather Patta", description:"", keywords:"", imageUrl:"",
};

export default function PageCatalogPro({ toast, products=[], tenant }) {
  const [tab,         setTab]        = useState("catalog");
  const [search,      setSearch]     = useState("");
  const [catFilter,   setCatFilter]  = useState("all");
  const [viewMode,    setViewMode]   = useState("grid"); // grid | table
  const [showModal,   setShowModal]  = useState(false);
  const [editProd,    setEditProd]   = useState(null);
  const [form,        setForm]       = useState(EMPTY_PRODUCT);

  // AI Content
  const [aiImage,     setAiImage]    = useState(null);
  const [aiLoading,   setAiLoading]  = useState(false);
  const [aiResult,    setAiResult]   = useState(null);
  const [aiPlatform,  setAiPlatform] = useState("all");
  const [extraInfo,   setExtraInfo]  = useState({ sku:"", mrp:"", costPrice:"", hsn:"42033000", category:"Belts", color:"", size:"", material:"Genuine Leather", brand:tenant?.storeName||"Leather Patta" });

  // Image formatter
  const [imgFile,     setImgFile]    = useState(null);
  const [imgPreview,  setImgPreview] = useState(null);
  const [imgPlatform, setImgPlatform]= useState("amazon");
  const [processing,  setProcessing] = useState(false);
  const [keepBg,      setKeepBg]      = useState(false);
  const [processedImg,setProcessedImg]=useState(null);
  const canvasRef = useRef(null);
  const fileRef   = useRef(null);
  const imgFileRef= useRef(null);

  // SKU Map
  const [skuMap,      setSkuMap]     = useState(() => {
    try { return JSON.parse(localStorage.getItem("sellsync_skumap")||"{}"); } catch { return {}; }
  });
  const [skuEdits,    setSkuEdits]   = useState({});

  // Bulk
  const [bulkText,    setBulkText]   = useState("");
  const [bulkResult,  setBulkResult] = useState(null);

  // Local catalog (from products prop + localStorage additions)
  const [localProds,  setLocalProds] = useState(() => {
    try { return JSON.parse(localStorage.getItem("3june_catalog")||"[]"); } catch { return []; }
  });

  const allProds = [...products, ...localProds];

  // Filtered
  const filtered = allProds.filter(p => {
    const q = search.toLowerCase();
    const matchSearch = !q || p.name?.toLowerCase().includes(q) || p.shopifySku?.toLowerCase().includes(q) || p.sku?.toLowerCase().includes(q);
    const matchCat    = catFilter==="all" || p.category===catFilter;
    return matchSearch && matchCat;
  });

  const setF = (k,v) => setForm(f=>({...f,[k]:v}));

  const saveProduct = () => {
    if (!form.name || !form.sku) { toast("Name and SKU are required","error"); return; }
    let updated;
    if (editProd) {
      updated = localProds.map(p=>p.id===editProd.id?{...form,id:editProd.id}:p);
    } else {
      updated = [{...form, id:Date.now(), source:"manual", createdAt:new Date().toISOString()}, ...localProds];
    }
    setLocalProds(updated);
    localStorage.setItem("3june_catalog", JSON.stringify(updated));
    setShowModal(false);
    setEditProd(null);
    setForm(EMPTY_PRODUCT);
    toast(editProd?"Product updated ✓":"Product added ✓","success");
  };

  const deleteProduct = (id) => {
    if (!window.confirm("Delete this product?")) return;
    const updated = localProds.filter(p=>p.id!==id);
    setLocalProds(updated);
    localStorage.setItem("3june_catalog", JSON.stringify(updated));
    toast("Deleted","success");
  };

  const openEdit = (p) => { setEditProd(p); setForm({...EMPTY_PRODUCT,...p}); setShowModal(true); };
  const openAdd  = () => { setEditProd(null); setForm(EMPTY_PRODUCT); setShowModal(true); };

  // AI Content generation
  const generateContent = async () => {
    setAiLoading(true);
    setAiResult(null);
    try {
      let imgData = null;
      if (aiImage?.base64) imgData = aiImage.base64;

      const platformInstructions = {
        all:      "Generate for all platforms: Amazon, Flipkart, Myntra, Meesho and Shopify",
        amazon:   "Generate specifically for Amazon India marketplace",
        flipkart: "Generate specifically for Flipkart marketplace",
        myntra:   "Generate specifically for Myntra fashion platform",
        meesho:   "Generate specifically for Meesho marketplace",
        shopify:  "Generate specifically for a Shopify store",
      };

      const data = await authFetch("/ai/text", {
        method: "POST",
        body: JSON.stringify({
          prompt: `You are an expert ecommerce product listing specialist for Indian marketplaces.

${imgData?"Analyze this product image and ":""}Generate optimized product content for an Indian ecommerce seller.

Product Details:
- Brand: ${extraInfo.brand}
- Category: ${extraInfo.category}
- Material: ${extraInfo.material}
- Color: ${extraInfo.color||"Not specified"}
- Size: ${extraInfo.size||"Standard"}
- MRP: Rs.${extraInfo.mrp||"Not specified"}
- HSN Code: ${extraInfo.hsn}

Platform: ${platformInstructions[aiPlatform]}

Generate the following in JSON format:
{
  "title": "SEO-optimized product title (max 200 chars)",
  "description": "Compelling product description (150-200 words)",
  "bulletPoints": ["Feature 1", "Feature 2", "Feature 3", "Feature 4", "Feature 5"],
  "keywords": "comma-separated relevant search keywords (15-20 keywords)",
  "specifications": {"Material":"...", "Closure":"...", "Width":"...", "Occasion":"...", "Ideal For":"..."},
  "seoTitle": "Short SEO title for Shopify (60 chars max)",
  "metaDescription": "Meta description for SEO (155 chars max)"
}

Return ONLY valid JSON, no other text.`,
          maxTokens: 1200,
        }),
      });
      const text   = (data.result||"").replace(/```json|```/g,"").trim();
      const result = JSON.parse(text);
      setAiResult(result);
      toast("✅ Content generated!","success");
    } catch(e) { toast("AI error: "+e.message,"error"); }
    setAiLoading(false);
  };

  // Image formatter — resize to platform specs
  const formatImage = useCallback(() => {
    if (!imgFile) { toast("Upload an image first","error"); return; }
    setProcessing(true);

    const specs = {
      amazon:   { w:2000, h:2000, bg:"#ffffff", label:"Amazon (2000×2000, white BG)" },
      flipkart: { w:1000, h:1000, bg:"#ffffff", label:"Flipkart (1000×1000)" },
      myntra:   { w:1080, h:1440, bg:"#f5f5f5", label:"Myntra (1080×1440, portrait)" },
      meesho:   { w:800,  h:800,  bg:"#ffffff", label:"Meesho (800×800)" },
      shopify:  { w:2048, h:2048, bg:"#ffffff", label:"Shopify (2048×2048)" },
      instagram:{ w:1080, h:1080, bg:"#ffffff", label:"Instagram (1080×1080)" },
    };

    const spec   = specs[imgPlatform] || specs.amazon;
    const canvas = document.createElement("canvas");
    canvas.width  = spec.w;
    canvas.height = spec.h;
    const ctx    = canvas.getContext("2d");

    // Fill background (skip if keeping original)
    if (!keepBg) {
      ctx.fillStyle = spec.bg;
      ctx.fillRect(0,0,spec.w,spec.h);
    }

    const img = new Image();
    img.onload = () => {
      // Center-fit image maintaining aspect ratio
      const ratio = Math.min(spec.w*0.85/img.width, spec.h*0.85/img.height);
      const newW  = img.width  * ratio;
      const newH  = img.height * ratio;
      const x     = (spec.w - newW)/2;
      const y     = (spec.h - newH)/2;
      ctx.drawImage(img, x, y, newW, newH);
      const fmt = keepBg ? 'image/png' : 'image/jpeg';
      const url = canvas.toDataURL(fmt, 0.92);
      setProcessedImg({ url, label:spec.label+(keepBg?' · Original BG':''), w:spec.w, h:spec.h });
      setProcessing(false);
      toast(`✅ Formatted for ${spec.label}`,"success");
    };
    img.src = imgPreview;
  }, [imgFile, imgPreview, imgPlatform]);

  const downloadProcessed = () => {
    if (!processedImg) return;
    const a = document.createElement("a");
    a.href     = processedImg.url;
    a.download = `product_${imgPlatform}_${Date.now()}.jpg`;
    a.click();
    toast("Image downloaded ✓","success");
  };

  // SKU Map save
  const saveSkuMap = (shopifySku) => {
    const edits   = skuEdits[shopifySku] || {};
    const updated = { ...skuMap, [shopifySku]:{ ...(skuMap[shopifySku]||{}), ...edits } };
    setSkuMap(updated);
    localStorage.setItem("sellsync_skumap", JSON.stringify(updated));
    setSkuEdits(prev => { const n={...prev}; delete n[shopifySku]; return n; });
    toast("SKU mapping saved ✓","success");
  };

  const saveAllSkuMaps = () => {
    const updated = { ...skuMap };
    Object.entries(skuEdits).forEach(([sku, edits]) => { updated[sku] = { ...(skuMap[sku]||{}), ...edits }; });
    setSkuMap(updated);
    localStorage.setItem("sellsync_skumap", JSON.stringify(updated));
    setSkuEdits({});
    toast("All SKU mappings saved ✓","success");
  };

  // Bulk import
  const parseBulk = () => {
    if (!bulkText.trim()) { toast("Paste product data first","error"); return; }
    const lines   = bulkText.trim().split("\n").filter(l=>l.trim());
    const parsed  = lines.map((line,i) => {
      const parts = line.split("\t").map(s=>s.trim());
      return { id:Date.now()+i, name:parts[0]||"", sku:parts[1]||"", mrp:parts[2]||"", price:parts[3]||"", category:parts[4]||"Formal Belts", source:"bulk" };
    }).filter(p=>p.name);
    setBulkResult(parsed);
    toast(`Parsed ${parsed.length} products — review before saving`,"success");
  };

  const saveBulk = () => {
    if (!bulkResult?.length) return;
    const updated = [...bulkResult, ...localProds];
    setLocalProds(updated);
    localStorage.setItem("3june_catalog", JSON.stringify(updated));
    setBulkResult(null);
    setBulkText("");
    toast(`${bulkResult.length} products added ✓`,"success");
  };

  const exportCatalog = () => {
    const headers = ["Name","SKU","Category","MRP","Price","Cost Price","Material","Color","Size","HSN","GST%","Description"];
    const rows    = allProds.map(p=>[p.name,p.shopifySku||p.sku||"",p.category||"",p.mrp||"",p.price||"",p.costPrice||"",p.material||"",p.color||"",p.size||"",p.hsn||"42033000",p.gstRate||"18",p.description||""]);
    const csv     = [headers,...rows].map(r=>r.map(v=>`"${v}"`).join(",")).join("\n");
    const a       = document.createElement("a"); a.href="data:text/csv;charset=utf-8,"+encodeURIComponent(csv); a.download="catalog.csv"; a.click();
    toast("Catalog exported ✓","success");
  };

  // Stats
  const totalProds     = allProds.length;
  const withImages     = allProds.filter(p=>p.imageUrl).length;
  const withDesc       = allProds.filter(p=>p.description).length;
  const byCategory     = {};
  allProds.forEach(p=>{ byCategory[p.category||"Other"] = (byCategory[p.category||"Other"]||0)+1; });

  return (
    <div>
      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14, flexWrap:"wrap", gap:10 }}>
        <div>
          <div style={{ fontWeight:700, fontSize:16 }}>🗂️ Catalog & PIM</div>
          <div style={{ fontSize:12, color:"#9ca3af" }}>Product catalog, AI content, image formatter and SKU mapping</div>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <button style={S.btnOut} onClick={exportCatalog}>⬇ Export CSV</button>
          <button style={S.btn()} onClick={openAdd}>+ Add Product</button>
        </div>
      </div>

      {/* KPIs */}
      <div className="kpi-grid" className="catalog-product-grid" style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:14 }}>
        {[
          { label:"Total Products",  value:totalProds,  color:"#2563eb", icon:"📦" },
          { label:"With Images",     value:withImages,  color:"#15803d", icon:"🖼️" },
          { label:"With Description",value:withDesc,    color:"#F47920", icon:"📝" },
          { label:"Categories",      value:Object.keys(byCategory).length, color:"#7e22ce", icon:"🏷️" },
        ].map(k=>(
          <div key={k.label} style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", padding:"11px 14px", borderLeft:"3px solid "+k.color, display:"flex", alignItems:"center", gap:10 }}>
            <span style={{ fontSize:20 }}>{k.icon}</span>
            <div>
              <div style={{ fontSize:9, color:"#9ca3af", fontWeight:700, textTransform:"uppercase" }}>{k.label}</div>
              <div style={{ fontSize:20, fontWeight:700, color:k.color }}>{k.value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display:"flex", borderBottom:"1px solid #e5e7eb", marginBottom:14, overflowX:"auto" }}>
        {TABS.map(t=>(
          <button key={t.id} style={{ padding:"9px 16px", border:"none", borderBottom:tab===t.id?"2px solid #F47920":"2px solid transparent", background:"none", color:tab===t.id?"#F47920":"#6b7280", fontWeight:tab===t.id?600:400, fontSize:12, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}
            onClick={()=>setTab(t.id)}>{t.label}</button>
        ))}
      </div>

      {/* ══ CATALOG ══ */}
      {tab === "catalog" && (
        <div>
          {/* Filters */}
          <div style={{ display:"flex", gap:8, marginBottom:12, flexWrap:"wrap", alignItems:"center" }}>
            <input style={{ ...S.input, width:220 }} placeholder="🔍 Search product or SKU…" value={search} onChange={e=>setSearch(e.target.value)}/>
            <select style={{ ...S.input, width:"auto" }} value={catFilter} onChange={e=>setCatFilter(e.target.value)}>
              <option value="all">All Categories</option>
              {CATEGORIES.map(c=><option key={c}>{c}</option>)}
            </select>
            <div style={{ display:"flex", gap:4, marginLeft:"auto" }}>
              <button style={{ ...S.btnOut, padding:"6px 10px", background:viewMode==="grid"?"#f3f4f6":"#fff" }} onClick={()=>setViewMode("grid")}>⊞</button>
              <button style={{ ...S.btnOut, padding:"6px 10px", background:viewMode==="table"?"#f3f4f6":"#fff" }} onClick={()=>setViewMode("table")}>☰</button>
            </div>
            <span style={{ fontSize:11, color:"#9ca3af" }}>{filtered.length} products</span>
          </div>

          {/* Grid view */}
          {viewMode==="grid" && (
            <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12 }}>
              {filtered.length===0 ? (
                <div style={{ gridColumn:"1/-1", padding:60, textAlign:"center", color:"#9ca3af" }}>
                  <div style={{ fontSize:40, marginBottom:10 }}>📦</div>
                  <div style={{ marginBottom:12 }}>No products yet</div>
                  <button style={S.btn()} onClick={openAdd}>+ Add Your First Product</button>
                </div>
              ) : filtered.map(p=>(
                <div key={p.id||p.shopifyId} style={{ ...S.card, overflow:"hidden", cursor:"pointer" }} onClick={()=>openEdit({...p,sku:p.shopifySku||p.sku})}>
                  {p.imageUrl
                    ? <img src={p.imageUrl} alt="" style={{ width:"100%", height:160, objectFit:"cover" }}/>
                    : <div style={{ width:"100%", height:160, background:"#f3f4f6", display:"flex", alignItems:"center", justifyContent:"center", fontSize:40 }}>📦</div>
                  }
                  <div style={{ padding:"10px 12px" }}>
                    <div style={{ fontWeight:600, fontSize:12, marginBottom:4, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{p.name}</div>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
                      <span style={{ fontFamily:"monospace", fontSize:10, color:"#F47920" }}>{p.shopifySku||p.sku||"—"}</span>
                      <span style={{ fontWeight:700, fontSize:12 }}>₹{p.price||p.mrp||0}</span>
                    </div>
                    <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
                      {p.category && <span style={S.tag("#7e22ce")}>{p.category}</span>}
                      {p.material && <span style={S.tag("#2563eb")}>{p.material.split(" ")[0]}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Table view */}
          {viewMode==="table" && (
            <div style={{ ...S.card, overflow:"hidden" }}>
              <div style={{ overflowX:"auto" }}>
                <table style={{ width:"100%", borderCollapse:"collapse" }}>
                  <thead><tr>{["","Product","SKU","Category","MRP","Cost","Material","Color","HSN","Actions"].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {filtered.length===0 ? (
                      <tr><td colSpan={10} style={{ ...S.td, textAlign:"center", padding:40, color:"#9ca3af" }}>No products</td></tr>
                    ) : filtered.map((p,i)=>(
                      <tr key={p.id||p.shopifyId} style={{ background:i%2?"#fafafa":"#fff" }}
                        onMouseEnter={e=>e.currentTarget.style.background="#fff8f2"}
                        onMouseLeave={e=>e.currentTarget.style.background=i%2?"#fafafa":"#fff"}>
                        <td style={S.td}>
                          {p.imageUrl
                            ? <img src={p.imageUrl} alt="" style={{ width:36,height:36,borderRadius:6,objectFit:"cover" }}/>
                            : <div style={{ width:36,height:36,borderRadius:6,background:"#f3f4f6",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18 }}>📦</div>
                          }
                        </td>
                        <td style={{ ...S.td, maxWidth:200, fontWeight:500 }}>
                          <div style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{p.name}</div>
                          {p.description && <div style={{ fontSize:10, color:"#9ca3af", marginTop:2, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{p.description}</div>}
                        </td>
                        <td style={{ ...S.td, fontFamily:"monospace", fontSize:11, color:"#F47920" }}>{p.shopifySku||p.sku||"—"}</td>
                        <td style={S.td}><span style={S.tag("#7e22ce")}>{p.category||"—"}</span></td>
                        <td style={{ ...S.td, fontWeight:600 }}>₹{p.mrp||p.price||0}</td>
                        <td style={{ ...S.td, color:"#15803d" }}>{p.costPrice?"₹"+p.costPrice:"—"}</td>
                        <td style={S.td}>{p.material||"—"}</td>
                        <td style={S.td}>{p.color||"—"}</td>
                        <td style={{ ...S.td, fontFamily:"monospace", fontSize:10 }}>{p.hsn||"—"}</td>
                        <td style={S.td}>
                          <div style={{ display:"flex", gap:4 }}>
                            <button style={{ ...S.btn("#2563eb"), padding:"3px 8px", fontSize:10 }} onClick={()=>openEdit({...p,sku:p.shopifySku||p.sku})}>Edit</button>
                            {p.source==="manual" && <button style={{ ...S.btnOut, padding:"3px 8px", fontSize:10, color:"#dc2626" }} onClick={()=>deleteProduct(p.id)}>Del</button>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ══ AI CONTENT ══ */}
      {tab === "ai" && (
        <div className="ai-content-grid" className="image-format-grid" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
          {/* Left - Input */}
          <div>
            <div style={S.card}>
              <div style={S.hdr}><span>📸 Product Image (Optional)</span></div>
              <div style={{ padding:14 }}>
                {!imgPreview ? (
                  <div onClick={()=>fileRef.current?.click()} style={{ border:"2px dashed #e5e7eb", borderRadius:8, padding:30, textAlign:"center", cursor:"pointer", background:"#fafafa" }}>
                    <input ref={fileRef} type="file" accept="image/*" style={{ display:"none" }}
                      onChange={e=>{
                        const file = e.target.files[0]; if (!file) return;
                        const reader = new FileReader();
                        reader.onload = ev => {
                          setAiImage({ preview:ev.target.result, base64:ev.target.result.split(",")[1], file });
                          setImgPreview(ev.target.result);
                        };
                        reader.readAsDataURL(file);
                      }}/>
                    <div style={{ fontSize:32, marginBottom:8 }}>📸</div>
                    <div style={{ fontSize:12, color:"#6b7280" }}>Upload product image for AI to analyze</div>
                    <div style={{ fontSize:10, color:"#9ca3af", marginTop:4 }}>JPG, PNG — optional but improves accuracy</div>
                  </div>
                ) : (
                  <div style={{ position:"relative" }}>
                    <img src={imgPreview} alt="" style={{ width:"100%", maxHeight:200, objectFit:"contain", borderRadius:8, background:"#f3f4f6" }}/>
                    <button onClick={()=>{ setAiImage(null); setImgPreview(null); }} style={{ position:"absolute", top:6, right:6, background:"rgba(0,0,0,0.5)", border:"none", color:"#fff", borderRadius:"50%", width:24, height:24, cursor:"pointer", fontSize:14 }}>×</button>
                  </div>
                )}
              </div>
            </div>

            <div style={S.card}>
              <div style={S.hdr}><span>📝 Product Details</span></div>
              <div style={{ padding:14, display:"flex", flexDirection:"column", gap:10 }}>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                  <div>
                    <label style={S.label}>Brand</label>
                    <input style={{ ...S.input, width:"100%" }} value={extraInfo.brand} onChange={e=>setExtraInfo(f=>({...f,brand:e.target.value}))}/>
                  </div>
                  <div>
                    <label style={S.label}>Category</label>
                    <input style={{ ...S.input, width:"100%" }} value={extraInfo.category} onChange={e=>setExtraInfo(f=>({...f,category:e.target.value}))} placeholder="Belts"/>
                  </div>
                  <div>
                    <label style={S.label}>Material</label>
                    <input style={{ ...S.input, width:"100%" }} value={extraInfo.material} onChange={e=>setExtraInfo(f=>({...f,material:e.target.value}))}/>
                  </div>
                  <div>
                    <label style={S.label}>Color</label>
                    <input style={{ ...S.input, width:"100%" }} value={extraInfo.color} onChange={e=>setExtraInfo(f=>({...f,color:e.target.value}))} placeholder="Black"/>
                  </div>
                  <div>
                    <label style={S.label}>MRP</label>
                    <input style={{ ...S.input, width:"100%" }} value={extraInfo.mrp} onChange={e=>setExtraInfo(f=>({...f,mrp:e.target.value}))} placeholder="₹999"/>
                  </div>
                  <div>
                    <label style={S.label}>Size</label>
                    <input style={{ ...S.input, width:"100%" }} value={extraInfo.size} onChange={e=>setExtraInfo(f=>({...f,size:e.target.value}))} placeholder="32-44"/>
                  </div>
                </div>
                <div>
                  <label style={S.label}>Platform</label>
                  <select style={{ ...S.input, width:"100%" }} value={aiPlatform} onChange={e=>setAiPlatform(e.target.value)}>
                    <option value="all">All Platforms</option>
                    {PLATFORMS.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <button style={{ ...S.btn("#6366f1"), width:"100%", padding:10, opacity:aiLoading?0.7:1 }} onClick={generateContent} disabled={aiLoading}>
                  {aiLoading?"🤖 Generating…":"🤖 Generate Listing Content"}
                </button>
              </div>
            </div>
          </div>

          {/* Right - Output */}
          <div>
            {!aiResult ? (
              <div style={{ ...S.card, padding:40, textAlign:"center", color:"#9ca3af" }}>
                <div style={{ fontSize:40, marginBottom:10 }}>🤖</div>
                <div style={{ fontWeight:600, marginBottom:6 }}>AI Content Generator</div>
                <div style={{ fontSize:12 }}>Fill in product details and click Generate</div>
              </div>
            ) : (
              <div>
                <div style={S.card}>
                  <div style={S.hdr}>
                    <span>📦 Product Title</span>
                    <button style={{ ...S.btnOut, fontSize:10 }} onClick={()=>{navigator.clipboard.writeText(aiResult.title||""); toast("Copied!","success");}}>Copy</button>
                  </div>
                  <div style={{ padding:14, fontSize:13, fontWeight:500, color:"#1A3A5C", lineHeight:1.5 }}>{aiResult.title}</div>
                </div>

                <div style={S.card}>
                  <div style={S.hdr}>
                    <span>📋 Bullet Points</span>
                    <button style={{ ...S.btnOut, fontSize:10 }} onClick={()=>{navigator.clipboard.writeText((aiResult.bulletPoints||[]).join("\n")); toast("Copied!","success");}}>Copy All</button>
                  </div>
                  <div style={{ padding:14 }}>
                    {(aiResult.bulletPoints||[]).map((b,i)=>(
                      <div key={i} style={{ display:"flex", gap:8, marginBottom:8, fontSize:12, lineHeight:1.5 }}>
                        <span style={{ color:"#F47920", fontWeight:700, flexShrink:0 }}>✓</span>
                        <span>{b}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={S.card}>
                  <div style={S.hdr}>
                    <span>📝 Description</span>
                    <button style={{ ...S.btnOut, fontSize:10 }} onClick={()=>{navigator.clipboard.writeText(aiResult.description||""); toast("Copied!","success");}}>Copy</button>
                  </div>
                  <div style={{ padding:14, fontSize:12, lineHeight:1.6, color:"#374151" }}>{aiResult.description}</div>
                </div>

                <div style={S.card}>
                  <div style={S.hdr}><span>🔍 Keywords</span></div>
                  <div style={{ padding:14, display:"flex", flexWrap:"wrap", gap:5 }}>
                    {(aiResult.keywords||"").split(",").map((k,i)=>(
                      <span key={i} style={{ ...S.tag("#2563eb"), cursor:"pointer" }} onClick={()=>{navigator.clipboard.writeText(k.trim()); toast("Copied!","success");}}>{k.trim()}</span>
                    ))}
                  </div>
                </div>

                {aiResult.specifications && (
                  <div style={S.card}>
                    <div style={S.hdr}><span>📐 Specifications</span></div>
                    <div style={{ padding:14 }}>
                      {Object.entries(aiResult.specifications).map(([k,v])=>(
                        <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"5px 0", borderBottom:"1px solid #f9fafb", fontSize:12 }}>
                          <span style={{ color:"#6b7280", fontWeight:600 }}>{k}</span>
                          <span>{v}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {aiResult.seoTitle && (
                  <div style={S.card}>
                    <div style={S.hdr}><span>🔎 SEO Meta</span></div>
                    <div style={{ padding:14 }}>
                      <div style={{ marginBottom:8 }}>
                        <div style={{ fontSize:10, color:"#9ca3af", marginBottom:2 }}>SEO Title ({(aiResult.seoTitle||"").length} chars)</div>
                        <div style={{ fontSize:12, fontWeight:600, color:"#1d4ed8" }}>{aiResult.seoTitle}</div>
                      </div>
                      <div>
                        <div style={{ fontSize:10, color:"#9ca3af", marginBottom:2 }}>Meta Description ({(aiResult.metaDescription||"").length} chars)</div>
                        <div style={{ fontSize:12, color:"#374151" }}>{aiResult.metaDescription}</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══ IMAGE FORMATTER ══ */}
      {tab === "images" && (
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
          <div>
            <div style={S.card}>
              <div style={S.hdr}><span>📁 Upload Image</span></div>
              <div style={{ padding:14 }}>
                {!imgFile ? (
                  <div onClick={()=>imgFileRef.current?.click()} style={{ border:"2px dashed #e5e7eb", borderRadius:8, padding:40, textAlign:"center", cursor:"pointer", background:"#fafafa" }}>
                    <input ref={imgFileRef} type="file" accept="image/*" style={{ display:"none" }}
                      onChange={e=>{
                        const f = e.target.files[0]; if (!f) return;
                        setImgFile(f);
                        setImgPreview(URL.createObjectURL(f));
                        setProcessedImg(null);
                      }}/>
                    <div style={{ fontSize:40, marginBottom:8 }}>🖼️</div>
                    <div style={{ fontSize:13, fontWeight:600, marginBottom:4 }}>Drop product image here</div>
                    <div style={{ fontSize:11, color:"#9ca3af" }}>JPG or PNG — any size</div>
                  </div>
                ) : (
                  <div>
                    <img src={imgPreview} alt="" style={{ width:"100%", maxHeight:220, objectFit:"contain", borderRadius:8, background:"#f3f4f6", marginBottom:10 }}/>
                    <button style={{ ...S.btnOut, fontSize:11, width:"100%" }} onClick={()=>{ setImgFile(null); setImgPreview(null); setProcessedImg(null); }}>✕ Remove</button>
                  </div>
                )}
              </div>
            </div>

            <div style={S.card}>
              <div style={S.hdr}><span>⚙️ Format Settings</span></div>
              <div style={{ padding:14 }}>
                <label style={S.label}>Target Platform</label>
                <select style={{ ...S.input, width:"100%", marginBottom:12 }} value={imgPlatform} onChange={e=>{ setImgPlatform(e.target.value); setProcessedImg(null); }}>
                  <option value="amazon">Amazon (2000×2000, white BG)</option>
                  <option value="flipkart">Flipkart (1000×1000, white BG)</option>
                  <option value="myntra">Myntra (1080×1440, portrait)</option>
                  <option value="meesho">Meesho (800×800, white BG)</option>
                  <option value="shopify">Shopify (2048×2048)</option>
                  <option value="instagram">Instagram (1080×1080)</option>
                </select>
                <label style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12, cursor:"pointer", padding:"10px 12px", borderRadius:8, border:"1px solid "+(keepBg?"#F47920":"#e5e7eb"), background:keepBg?"#fff8f2":"#fff" }}>
                  <input type="checkbox" checked={keepBg} onChange={e=>{ setKeepBg(e.target.checked); setProcessedImg(null); }} style={{ width:16, height:16, accentColor:"#F47920", cursor:"pointer" }}/>
                  <div>
                    <div style={{ fontSize:12, fontWeight:600, color:keepBg?"#F47920":"#374151" }}>Keep original background</div>
                    <div style={{ fontSize:10, color:"#9ca3af", marginTop:1 }}>Resizes only — does not add white background</div>
                  </div>
                </label>
                <button style={{ ...S.btn("#7e22ce"), width:"100%", opacity:processing?0.7:1 }} onClick={formatImage} disabled={!imgFile||processing}>
                  {processing?"⏳ Processing…":"🎨 Format Image"}
                </button>
              </div>
            </div>
          </div>

          <div>
            {!processedImg ? (
              <div style={{ ...S.card, padding:60, textAlign:"center", color:"#9ca3af" }}>
                <div style={{ fontSize:40, marginBottom:10 }}>🖼️</div>
                <div>Upload an image and click Format</div>
              </div>
            ) : (
              <div style={S.card}>
                <div style={S.hdr}>
                  <span>✅ {processedImg.label}</span>
                  <button style={S.btn("#15803d")} onClick={downloadProcessed}>⬇ Download</button>
                </div>
                <div style={{ padding:14 }}>
                  <img src={processedImg.url} alt="" style={{ width:"100%", maxHeight:300, objectFit:"contain", borderRadius:8, background:"#f3f4f6", marginBottom:10 }}/>
                  <div style={{ display:"flex", gap:10, fontSize:12, color:"#6b7280" }}>
                    <span>📐 {processedImg.w}×{processedImg.h}px</span>
                    <span>✅ Platform-optimized</span>
                  </div>
                </div>
              </div>
            )}

            {/* Platform specs reference */}
            <div style={S.card}>
              <div style={S.hdr}><span>📋 Platform Image Specs</span></div>
              <div style={{ padding:14 }}>
                {[
                  { plat:"Amazon",   spec:"2000×2000px, white background, product fills 85% of frame" },
                  { plat:"Flipkart", spec:"1000×1000px, white background, no watermarks" },
                  { plat:"Myntra",   spec:"1080×1440px (portrait), product on plain background" },
                  { plat:"Meesho",   spec:"800×800px, clear product view" },
                  { plat:"Shopify",  spec:"2048×2048px recommended, any background" },
                ].map(r=>(
                  <div key={r.plat} style={{ padding:"7px 0", borderBottom:"1px solid #f9fafb", fontSize:12 }}>
                    <span style={{ fontWeight:600, color:"#374151", marginRight:8 }}>{r.plat}:</span>
                    <span style={{ color:"#6b7280" }}>{r.spec}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══ SKU MAP ══ */}
      {tab === "skumap" && (
        <div>
          <div style={{ background:"#eff6ff", borderRadius:8, padding:"10px 14px", marginBottom:12, fontSize:12, color:"#1e40af" }}>
            💡 Map your Shopify SKUs to Amazon, Flipkart, Myntra and Meesho SKUs for inventory sync. Changes save locally.
          </div>

          <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:10 }}>
            {Object.keys(skuEdits).length>0 && (
              <button style={S.btn()} onClick={saveAllSkuMaps}>💾 Save All Changes ({Object.keys(skuEdits).length})</button>
            )}
          </div>

          <div style={{ ...S.card, overflow:"hidden" }}>
            <div style={{ overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                <thead>
                  <tr>
                    {["Product","Shopify SKU","Amazon SKU","Flipkart SKU","Myntra SKU","Meesho SKU",""].map(h=><th key={h} style={S.th}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {allProds.length===0 ? (
                    <tr><td colSpan={7} style={{ ...S.td, textAlign:"center", padding:40, color:"#9ca3af" }}>No products to map</td></tr>
                  ) : allProds.map((p,i)=>{
                    const shopifySku = p.shopifySku||p.sku||"";
                    const edits      = skuEdits[shopifySku]||{};
                    const map        = skuMap[shopifySku]||{};
                    const isDirty    = Object.keys(edits).length>0;
                    return (
                      <tr key={p.id||p.shopifyId||i} style={{ background:i%2?"#fafafa":"#fff" }}>
                        <td style={S.td}>
                          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                            {p.imageUrl ? <img src={p.imageUrl} alt="" style={{ width:28,height:28,borderRadius:5,objectFit:"cover" }}/> : <div style={{ width:28,height:28,borderRadius:5,background:"#f3f4f6",display:"flex",alignItems:"center",justifyContent:"center",fontSize:13 }}>📦</div>}
                            <span style={{ fontSize:12, fontWeight:500, maxWidth:130, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{p.name}</span>
                          </div>
                        </td>
                        <td style={S.td}><span style={{ fontFamily:"monospace", fontSize:11, background:"#f3f4f6", padding:"2px 8px", borderRadius:5, color:"#F47920" }}>{shopifySku||"—"}</span></td>
                        {["amazonSku","flipkartSku","myntraSku","meeshoSku"].map(field=>(
                          <td key={field} style={S.td}>
                            <input style={{ ...S.input, width:120, borderColor:edits[field]!==undefined?"#F47920":"#d1d5db" }}
                              placeholder="Enter SKU"
                              value={edits[field]!==undefined?edits[field]:(map[field]||"")}
                              onChange={e=>setSkuEdits(prev=>({...prev,[shopifySku]:{...(prev[shopifySku]||{}),[field]:e.target.value}}))}/>
                          </td>
                        ))}
                        <td style={S.td}>
                          <button style={S.btn(isDirty?"#F47920":"#9ca3af")} onClick={()=>saveSkuMap(shopifySku)}>
                            {isDirty?"Save ✓":"Saved"}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ══ BULK OPERATIONS ══ */}
      {tab === "bulk" && (
        <div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
            <div style={S.card}>
              <div style={S.hdr}><span>📋 Bulk Import (Tab-separated)</span></div>
              <div style={{ padding:14 }}>
                <div style={{ background:"#f9fafb", borderRadius:8, padding:"8px 12px", fontSize:11, color:"#6b7280", marginBottom:10, fontFamily:"monospace" }}>
                  Format: Name [TAB] SKU [TAB] MRP [TAB] Price [TAB] Category<br/>
                  Example:<br/>
                  Black Formal Belt [TAB] CB-BLK-001 [TAB] 1499 [TAB] 899 [TAB] Formal Belts
                </div>
                <textarea
                  style={{ ...S.input, width:"100%", height:180, resize:"vertical", fontFamily:"monospace", fontSize:11 }}
                  placeholder="Paste product data here (tab-separated)..."
                  value={bulkText}
                  onChange={e=>setBulkText(e.target.value)}/>
                <div style={{ display:"flex", gap:8, marginTop:10 }}>
                  <button style={{ ...S.btn(), flex:1 }} onClick={parseBulk}>Parse & Preview</button>
                  <button style={S.btnOut} onClick={()=>{ setBulkText(""); setBulkResult(null); }}>Clear</button>
                </div>
              </div>
            </div>

            <div style={S.card}>
              <div style={S.hdr}><span>📤 Bulk Export</span></div>
              <div style={{ padding:14 }}>
                <div style={{ marginBottom:12 }}>
                  <div style={{ fontWeight:600, fontSize:12, marginBottom:6 }}>Export Options</div>
                  {[
                    { label:"Full Catalog CSV", desc:"All products with all fields", action:exportCatalog, color:"#15803d" },
                    { label:"SKU Mapping CSV", desc:"Shopify → all platform SKUs", action:()=>{
                        const rows = allProds.map(p=>[p.shopifySku||p.sku||"", (skuMap[p.shopifySku||""]||{}).amazonSku||"", (skuMap[p.shopifySku||""]||{}).flipkartSku||"", (skuMap[p.shopifySku||""]||{}).myntraSku||"", (skuMap[p.shopifySku||""]||{}).meeshoSku||""]);
                        const csv = [["Shopify SKU","Amazon SKU","Flipkart SKU","Myntra SKU","Meesho SKU"],...rows].map(r=>r.join(",")).join("\n");
                        const a = document.createElement("a"); a.href="data:text/csv;charset=utf-8,"+encodeURIComponent(csv); a.download="sku_map.csv"; a.click();
                        toast("SKU map exported ✓","success");
                      }, color:"#2563eb" },
                    { label:"Flipkart Template", desc:"Ready-to-upload Flipkart format", action:()=>{
                        const rows = allProds.map(p=>([p.name,p.shopifySku||"",p.mrp||p.price||"",p.price||"",p.description||"",p.hsn||"42033000","18",p.brand||"","Genuine Leather"]));
                        const csv = [["Product Title","Seller SKU","MRP","Selling Price","Description","HSN","GST %","Brand","Material"],...rows].map(r=>r.map(v=>`"${v}"`).join(",")).join("\n");
                        const a = document.createElement("a"); a.href="data:text/csv;charset=utf-8,"+encodeURIComponent(csv); a.download="flipkart_template.csv"; a.click();
                        toast("Flipkart template exported ✓","success");
                      }, color:"#1b57c0" },
                  ].map(opt=>(
                    <div key={opt.label} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 0", borderBottom:"1px solid #f3f4f6" }}>
                      <div>
                        <div style={{ fontWeight:600, fontSize:12 }}>{opt.label}</div>
                        <div style={{ fontSize:11, color:"#9ca3af" }}>{opt.desc}</div>
                      </div>
                      <button style={{ ...S.btn(opt.color), padding:"5px 12px", fontSize:11 }} onClick={opt.action}>⬇ Export</button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Bulk preview */}
          {bulkResult && (
            <div style={S.card}>
              <div style={S.hdr}>
                <span>👁 Preview — {bulkResult.length} products</span>
                <div style={{ display:"flex", gap:8 }}>
                  <button style={S.btn()} onClick={saveBulk}>✅ Save All</button>
                  <button style={S.btnOut} onClick={()=>setBulkResult(null)}>Cancel</button>
                </div>
              </div>
              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                <thead><tr>{["Name","SKU","MRP","Price","Category"].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {bulkResult.map((p,i)=>(
                    <tr key={i} style={{ background:i%2?"#fafafa":"#fff" }}>
                      <td style={S.td}>{p.name}</td>
                      <td style={{ ...S.td, fontFamily:"monospace", fontSize:11 }}>{p.sku}</td>
                      <td style={S.td}>{p.mrp?"₹"+p.mrp:"—"}</td>
                      <td style={S.td}>{p.price?"₹"+p.price:"—"}</td>
                      <td style={S.td}><span style={S.tag("#7e22ce")}>{p.category}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ══ ADD/EDIT PRODUCT MODAL ══ */}
      {showModal && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.4)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}
          onClick={e=>e.target===e.currentTarget&&setShowModal(false)}>
          <div style={{ background:"#fff", borderRadius:12, width:"100%", maxWidth:600, maxHeight:"90vh", overflow:"auto", boxShadow:"0 20px 60px rgba(0,0,0,0.2)" }}>
            <div style={{ padding:"14px 20px", borderBottom:"1px solid #e5e7eb", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <span style={{ fontWeight:700, fontSize:14 }}>{editProd?"Edit Product":"Add Product"}</span>
              <button onClick={()=>setShowModal(false)} style={{ background:"none", border:"none", fontSize:20, cursor:"pointer", color:"#9ca3af" }}>×</button>
            </div>
            <div style={{ padding:20, display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
              <div style={{ gridColumn:"1/-1" }}>
                <label style={S.label}>Product Name *</label>
                <input style={{ ...S.input, width:"100%" }} placeholder="Genuine Leather Formal Belt - Black" value={form.name} onChange={e=>setF("name",e.target.value)}/>
              </div>
              <div>
                <label style={S.label}>SKU *</label>
                <input style={{ ...S.input, width:"100%" }} placeholder="CB-BLK-001" value={form.sku} onChange={e=>setF("sku",e.target.value)}/>
              </div>
              <div>
                <label style={S.label}>Category</label>
                <select style={{ ...S.input, width:"100%" }} value={form.category} onChange={e=>setF("category",e.target.value)}>
                  {CATEGORIES.map(c=><option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label style={S.label}>MRP ₹</label>
                <input type="number" style={{ ...S.input, width:"100%" }} placeholder="1499" value={form.mrp} onChange={e=>setF("mrp",e.target.value)}/>
              </div>
              <div>
                <label style={S.label}>Selling Price ₹</label>
                <input type="number" style={{ ...S.input, width:"100%" }} placeholder="899" value={form.price} onChange={e=>setF("price",e.target.value)}/>
              </div>
              <div>
                <label style={S.label}>Cost Price ₹</label>
                <input type="number" style={{ ...S.input, width:"100%" }} placeholder="350" value={form.costPrice} onChange={e=>setF("costPrice",e.target.value)}/>
              </div>
              <div>
                <label style={S.label}>HSN Code</label>
                <input style={{ ...S.input, width:"100%" }} value={form.hsn} onChange={e=>setF("hsn",e.target.value)}/>
              </div>
              <div>
                <label style={S.label}>Material</label>
                <input style={{ ...S.input, width:"100%" }} placeholder="Genuine Leather" value={form.material} onChange={e=>setF("material",e.target.value)}/>
              </div>
              <div>
                <label style={S.label}>Color</label>
                <input style={{ ...S.input, width:"100%" }} placeholder="Black" value={form.color} onChange={e=>setF("color",e.target.value)}/>
              </div>
              <div>
                <label style={S.label}>Size</label>
                <input style={{ ...S.input, width:"100%" }} placeholder="32-44" value={form.size} onChange={e=>setF("size",e.target.value)}/>
              </div>
              <div>
                <label style={S.label}>Brand</label>
                <input style={{ ...S.input, width:"100%" }} placeholder="Leather Patta" value={form.brand} onChange={e=>setF("brand",e.target.value)}/>
              </div>
              <div style={{ gridColumn:"1/-1" }}>
                <label style={S.label}>Image URL</label>
                <input style={{ ...S.input, width:"100%" }} placeholder="https://..." value={form.imageUrl} onChange={e=>setF("imageUrl",e.target.value)}/>
              </div>
              <div style={{ gridColumn:"1/-1" }}>
                <label style={S.label}>Description</label>
                <textarea style={{ ...S.input, width:"100%", height:80, resize:"vertical" }} placeholder="Product description…" value={form.description} onChange={e=>setF("description",e.target.value)}/>
              </div>
              <div style={{ gridColumn:"1/-1" }}>
                <label style={S.label}>Keywords (comma separated)</label>
                <input style={{ ...S.input, width:"100%" }} placeholder="leather belt, formal belt, genuine leather…" value={form.keywords} onChange={e=>setF("keywords",e.target.value)}/>
              </div>
            </div>
            <div style={{ padding:"14px 20px", borderTop:"1px solid #e5e7eb", display:"flex", gap:10, justifyContent:"flex-end" }}>
              <button style={S.btnOut} onClick={()=>setShowModal(false)}>Cancel</button>
              <button style={S.btn()} onClick={saveProduct}>{editProd?"Update Product":"Add Product"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
